import { Link } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import { useAdminPlans } from '@/shared/api/admin-plans';
import {
  type AdminCoupon,
  type CouponKind,
  type CreateCouponInput,
  useAdminCoupons,
  useCreateCoupon,
  useSoftDeleteCoupon,
} from '@/shared/api/admin-coupons';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

export function CouponsPage() {
  const { data, isFetching } = useAdminCoupons({ limit: 100 });
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminCoupon | null>(null);
  const deleteMut = useSoftDeleteCoupon();

  return (
    <AdminLayout>
      <header className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Coupons</h1>
          <p className="text-sm text-neutral-400">
            Promotional codes.  Active codes apply at checkout (Phase 1.5+ when Stripe wires).
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
        >
          <Plus size={14} /> New coupon
        </button>
      </header>

      <div className="mb-2 text-xs text-neutral-500">
        {isFetching ? 'Loading…' : data ? `${data.total} total` : null}
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2 font-medium">Code</th>
              <th className="px-4 py-2 font-medium">Kind</th>
              <th className="px-4 py-2 font-medium">Discount</th>
              <th className="px-4 py-2 font-medium text-right">Redeemed</th>
              <th className="px-4 py-2 font-medium">Valid until</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {data?.items.map((c) => (
              <tr key={c.id} className="hover:bg-neutral-800/40">
                <td className="px-4 py-2">
                  <Link
                    to="/coupons/$id"
                    params={{ id: c.id }}
                    className="inline-block"
                  >
                    <code className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs uppercase text-orange-400 hover:bg-neutral-700">
                      {c.code}
                    </code>
                  </Link>
                  {c.notes ? <div className="mt-0.5 text-xs text-neutral-500">{c.notes}</div> : null}
                </td>
                <td className="px-4 py-2 text-neutral-300">{c.kind}</td>
                <td className="px-4 py-2 text-neutral-200">{formatDiscount(c)}</td>
                <td className="px-4 py-2 text-right text-neutral-300">
                  {c.redemptionCount}
                  {c.maxRedemptions !== null ? <span className="text-neutral-500"> / {c.maxRedemptions}</span> : null}
                </td>
                <td className="px-4 py-2 text-neutral-400">{c.validUntil ? formatDate(c.validUntil) : '—'}</td>
                <td className="px-4 py-2">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
                      c.isActive
                        ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
                        : 'bg-neutral-700/30 text-neutral-500 ring-neutral-700',
                    )}
                  >
                    {c.isActive ? 'active' : 'disabled'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  {c.isActive ? (
                    <button
                      onClick={() => setDeleteTarget(c)}
                      className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={11} /> Disable
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                  No coupons yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <CreateCouponDialog open={createOpen} onOpenChange={setCreateOpen} />

      {deleteTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title={`Disable ${deleteTarget.code}?`}
          description="Sets is_active=false.  Existing redemptions preserved.  Reversible by creating a new coupon with the same code."
          confirmLabel="Disable"
          tone="danger"
          pending={deleteMut.isPending}
          onConfirm={async (reason) => {
            await deleteMut.mutateAsync({ id: deleteTarget.id, reason });
            toast.success('Coupon disabled');
            setDeleteTarget(null);
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

// ── Create dialog ──────────────────────────────────────────────────────────

function CreateCouponDialog(props: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: plans } = useAdminPlans();
  const createMut = useCreateCoupon();

  const [code, setCode] = useState('');
  const [kind, setKind] = useState<CouponKind>('percent');
  const [percentOff, setPercentOff] = useState('');
  const [fixedOffCents, setFixedOffCents] = useState('');
  const [planGrantCode, setPlanGrantCode] = useState('');
  const [planGrantDurationDays, setPlanGrantDurationDays] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [firstTimeOnly, setFirstTimeOnly] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  if (!props.open) return null;

  function reset() {
    setCode('');
    setKind('percent');
    setPercentOff('');
    setFixedOffCents('');
    setPlanGrantCode('');
    setPlanGrantDurationDays('');
    setMaxRedemptions('');
    setValidUntil('');
    setFirstTimeOnly(false);
    setNotes('');
    setReason('');
  }

  function close() {
    props.onOpenChange(false);
    setTimeout(reset, 200);
  }

  const codeOk = code.trim().length >= 3;
  const reasonOk = reason.trim().length >= 10;
  const kindOk =
    kind === 'percent'
      ? Number(percentOff) >= 1 && Number(percentOff) <= 100
      : kind === 'fixed'
        ? Number(fixedOffCents) > 0
        : Boolean(planGrantCode) && Number(planGrantDurationDays) > 0;
  const canSubmit = codeOk && reasonOk && kindOk && !createMut.isPending;

  async function submit() {
    if (!canSubmit) return;
    const input: CreateCouponInput = {
      code: code.trim(),
      kind,
      reason: reason.trim(),
    };
    if (kind === 'percent') input.percentOff = Number(percentOff);
    if (kind === 'fixed') input.fixedOffCents = Number(fixedOffCents);
    if (kind === 'plan_grant') {
      input.planGrantCode = planGrantCode;
      input.planGrantDurationDays = Number(planGrantDurationDays);
    }
    if (maxRedemptions) input.maxRedemptions = Number(maxRedemptions);
    if (validUntil) input.validUntil = new Date(validUntil).toISOString();
    if (firstTimeOnly) input.firstTimeCustomerOnly = true;
    if (notes.trim()) input.notes = notes.trim();

    try {
      await createMut.mutateAsync(input);
      toast.success(`Coupon ${input.code.toUpperCase()} created`);
      close();
    } catch {
      /* toast'ed */
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-neutral-900 p-6 shadow-2xl ring-1 ring-neutral-800">
        <h2 className="mb-1 text-base font-semibold text-neutral-100">New coupon</h2>
        <p className="mb-4 text-sm text-neutral-400">
          Promo codes activate at customer checkout (when Stripe wires).  Until then, founder
          can hand-distribute to beta testers.
        </p>

        <Field label="Code (3+ chars, case-insensitive)">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="LAUNCH50"
            className="w-full rounded-md bg-neutral-800 px-3 py-2 font-mono text-sm uppercase text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <Field label="Kind">
          <div className="flex gap-2">
            <KindPill label="Percent off" active={kind === 'percent'} onClick={() => setKind('percent')} />
            <KindPill label="Fixed off" active={kind === 'fixed'} onClick={() => setKind('fixed')} />
            <KindPill label="Plan grant" active={kind === 'plan_grant'} onClick={() => setKind('plan_grant')} />
          </div>
        </Field>

        {kind === 'percent' ? (
          <Field label="Percent off (1–100)">
            <input
              type="number"
              min={1}
              max={100}
              value={percentOff}
              onChange={(e) => setPercentOff(e.target.value)}
              placeholder="50"
              className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        ) : kind === 'fixed' ? (
          <Field label="Fixed off (cents)">
            <input
              type="number"
              min={1}
              value={fixedOffCents}
              onChange={(e) => setFixedOffCents(e.target.value)}
              placeholder="500 ($5 off)"
              className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plan to grant">
              <select
                value={planGrantCode}
                onChange={(e) => setPlanGrantCode(e.target.value)}
                className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose…</option>
                {(plans ?? [])
                  .filter((p) => p.code !== 'free')
                  .map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Duration (days)">
              <input
                type="number"
                min={1}
                max={3650}
                value={planGrantDurationDays}
                onChange={(e) => setPlanGrantDurationDays(e.target.value)}
                placeholder="90"
                className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
              />
            </Field>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Max redemptions (optional)">
            <input
              type="number"
              min={1}
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="unlimited"
              className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
          </Field>
          <Field label="Valid until (optional)">
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        </div>

        <label className="my-3 flex items-center gap-2 text-sm text-neutral-300">
          <input
            type="checkbox"
            checked={firstTimeOnly}
            onChange={(e) => setFirstTimeOnly(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-600 bg-neutral-800"
          />
          First-time customer only
        </label>

        <Field label="Notes (admin only)">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Twitter launch promo."
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <Field label="Reason (≥10 chars; recorded in audit log)">
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-neutral-800 pt-4">
          <button
            onClick={close}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="rounded-md bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            {createMut.isPending ? 'Creating…' : 'Create coupon'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">{label}</label>
      {children}
    </div>
  );
}

function KindPill(props: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={props.onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-xs font-medium ring-1 transition-colors',
        props.active
          ? 'bg-orange-500/20 text-orange-300 ring-orange-500/50'
          : 'bg-neutral-800 text-neutral-300 ring-neutral-700 hover:bg-neutral-700',
      )}
    >
      {props.label}
    </button>
  );
}

function formatDiscount(c: AdminCoupon): string {
  if (c.kind === 'percent') return `${c.percentOff}% off`;
  if (c.kind === 'fixed') return `$${((c.fixedOffCents ?? 0) / 100).toFixed(2)} off`;
  return `${c.planGrantCode} for ${c.planGrantDurationDays}d`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
